import { generateKeyBetween, generateNKeysBetween } from './core/fractional';
import {
  DEFAULT_SIZE,
  labelFor,
  PIN_COLORS,
  RELATION_COLORS,
  ROPE_COLORS,
  type ConnectorProps,
  type GfxElement,
  type NoteColor,
  type NoteProps,
  type PhotoProps,
  type PinProps,
  type RopeProps,
} from './core/types';
import type { Entity, Evidence, Hypothesis, Relation, Target, TimelineEvent } from '../state/domain';

const COLOR_BY_TYPE: Record<string, NoteColor> = {
  person: 'blue',
  organization: 'purple',
  org: 'purple',
  email: 'yellow',
  phone: 'orange',
  domain: 'green',
  ip: 'red',
  social: 'orange',
  wallet: 'yellow',
  nameserver: 'green',
  location: 'orange',
};

let seq = 0;
function uid(prefix: string): string {
  seq += 1;
  return `${prefix}_${Date.now().toString(36)}_${seq.toString(36)}`;
}

export type IntelGraphInput = {
  targets: Target[];
  entities: Entity[];
  relations: Relation[];
  evidence: Evidence[];
  hypotheses: Hypothesis[];
  timeline?: TimelineEvent[];
};

/**
 * 将情报数据布局为「侦探墙」元素：
 * 对象卡片（带图钉与照片）、线索卡片、红线绳索关系、研判卡片。
 * 采用确定性分层布局，保证同一份数据布局稳定。
 */
export function buildIntelGraph(input: IntelGraphInput): GfxElement[] {
  const elements: GfxElement[] = [];
  const nodeIdByRef = new Map<string, string>();
  const boxByNode = new Map<string, { x: number; y: number; w: number; h: number }>();

  let z = generateKeyBetween(null, null);
  const nextZ = () => {
    z = generateKeyBetween(z, null);
    return z;
  };

  // 背景分组框
  const zones: { title: string; color: string; x: number; y: number; w: number; h: number }[] = [
    { title: '研判结论', color: '#a16207', x: -980, y: -900, w: 1160, h: 300 },
    { title: '对象档案', color: '#1e3a5f', x: -980, y: -460, w: 1160, h: 460 },
    { title: '线索与关联', color: '#6d28d9', x: -1180, y: 120, w: 1760, h: 760 },
  ];
  for (const zone of zones) {
    elements.push({
      id: uid('frame'),
      type: 'frame',
      x: zone.x,
      y: zone.y,
      w: zone.w,
      h: zone.h,
      rotate: 0,
      z: nextZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: { title: zone.title, color: zone.color },
    });
  }

  placeTargets(input.targets, nodeIdByRef, boxByNode, elements, nextZ);
  placeEntities(input.entities, nodeIdByRef, boxByNode, elements, nextZ);
  placeRelationRopes(input.relations, nodeIdByRef, boxByNode, elements, nextZ);
  placeHypotheses(input.hypotheses, nodeIdByRef, elements, nextZ);

  void generateNKeysBetween;
  return elements;
}

type Box = { x: number; y: number; w: number; h: number };
type Map2 = Map<string, string>;
type BoxMap = Map<string, Box>;

function pushNote(
  elements: GfxElement[],
  id: string,
  box: Box,
  nextZ: () => string,
  props: Partial<NoteProps> & { title: string; text: string; color: NoteColor },
  ref?: { kind: 'target' | 'entity' | 'evidence' | 'hypothesis'; id: string }
): void {
  elements.push({
    id,
    type: 'note',
    ...box,
    rotate: 0,
    z: nextZ(),
    group: null,
    locked: false,
    hidden: false,
    local: false,
    refKind: ref?.kind,
    refId: ref?.id,
    props: props as NoteProps,
  });
}

function pushPin(elements: GfxElement[], at: Box, color: string, nextZ: () => string, attachTo?: string): void {
  const size = DEFAULT_SIZE.pin;
  elements.push({
    id: uid('pin'),
    type: 'pin',
    x: at.x + at.w / 2 - size.w / 2,
    y: at.y - size.h / 2,
    ...size,
    rotate: 0,
    z: nextZ(),
    group: null,
    locked: false,
    hidden: false,
    local: false,
    attachTo,
    props: { color, kind: 'push' } as PinProps as unknown as Record<string, unknown>,
  });
}

function placeTargets(
  targets: Target[],
  nodeIdByRef: Map2,
  boxByNode: BoxMap,
  elements: GfxElement[],
  nextZ: () => string
): void {
  const size = DEFAULT_SIZE.note;
  const photoSize = DEFAULT_SIZE.photo;
  const gap = 420;
  const startX = -((targets.length - 1) * gap) / 2;

  targets.forEach((target, index) => {
    const x = startX + index * gap - size.w / 2;
    const y = -340;
    const id = `tg_${target.id}`;
    nodeIdByRef.set(target.id, id);
    boxByNode.set(id, { x, y, w: size.w, h: size.h });

    const risk = target.riskLevel;
    const color: NoteColor =
      risk === 'critical' || risk === 'high' ? 'red' : risk === 'medium' ? 'yellow' : 'blue';

    pushNote(
      elements,
      id,
      { x, y, w: size.w, h: size.h },
      nextZ,
      {
        title: target.codename ? `${target.name}（${target.codename}）` : target.name,
        text: [
          `类型：${target.kind === 'organization' ? '组织' : '个人'}`,
          target.role ? `职务：${target.role}` : '',
          target.org ? `组织：${target.org}` : '',
          target.location ? `位置：${target.location}` : '',
          target.priority && target.priority !== 'normal' ? `优先级：${target.priority}` : '',
          `风险：${target.riskScore}（${target.riskLevel}）`,
          target.summary ?? '',
        ]
          .filter(Boolean)
          .join('\n'),
        color,
      },
      { kind: 'target', id: target.id }
    );

    pushPin(elements, { x, y, w: size.w, h: size.h }, risk === 'high' || risk === 'critical' ? PIN_COLORS[0] : PIN_COLORS[1], nextZ, id);

    if (target.photo) {
      const px = x + size.w + 24;
      const py = y + 10;
      elements.push({
        id: uid('photo'),
        type: 'photo',
        x: px,
        y: py,
        w: photoSize.w,
        h: photoSize.h,
        rotate: index % 2 === 0 ? -2.5 : 2,
        z: nextZ(),
        group: null,
        locked: false,
        hidden: false,
        local: false,
        refKind: 'target',
        refId: target.id,
        props: {
          src: target.photo,
          caption: target.codename ?? target.name,
          frame: 'polaroid',
        } as PhotoProps as unknown as Record<string, unknown>,
      });
    }
  });
}

function placeEntities(
  entities: Entity[],
  nodeIdByRef: Map2,
  boxByNode: BoxMap,
  elements: GfxElement[],
  nextZ: () => string
): void {
  const size = DEFAULT_SIZE.note;
  const perRow = 6;
  const gapX = 280;
  const gapY = 210;

  entities.forEach((entity, index) => {
    const col = index % perRow;
    const row = Math.floor(index / perRow);
    const x = -900 + col * gapX;
    const y = 220 + row * gapY;
    const id = `en_${entity.id}`;
    nodeIdByRef.set(entity.id, id);
    boxByNode.set(id, { x, y, w: size.w, h: size.h });
    pushNote(
      elements,
      id,
      { x, y, w: size.w, h: size.h },
      nextZ,
      {
        title: `${labelFor(entity.type)}｜${entity.value ?? entity.label ?? ''}`,
        text: [
          entity.aliases.length ? `别名：${entity.aliases.join('、')}` : '',
          `置信度 ${(entity.confidence * 100).toFixed(0)}% · ${statusLabel(entity.status)}`,
          entity.source ? `来源 ${entity.source}` : '',
          entity.geo?.place ? `地点：${entity.geo.place}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
        color: COLOR_BY_TYPE[entity.type] ?? 'default',
      },
      { kind: 'entity', id: entity.id }
    );
    if (entity.status === 'verified') {
      pushPin(elements, { x, y, w: size.w, h: size.h }, PIN_COLORS[3], nextZ, id);
    }
  });
}

function statusLabel(status: string | undefined): string {
  const map: Record<string, string> = {
    unverified: '待核',
    verified: '已核',
    disputed: '存疑',
    inactive: '失效',
  };
  return map[status ?? ''] ?? status ?? '待核';
}

function placeRelationRopes(
  relations: Relation[],
  nodeIdByRef: Map2,
  _boxByNode: BoxMap,
  elements: GfxElement[],
  nextZ: () => string
): void {
  for (const relation of relations) {
    const sourceId = nodeIdByRef.get(relation.sourceId);
    const targetId = nodeIdByRef.get(relation.targetId);
    if (!sourceId || !targetId) continue;
    const color = RELATION_COLORS[relation.type] ?? ROPE_COLORS[0];
    const props: RopeProps = {
      // 系在卡片顶部图钉处
      source: { id: sourceId, anchor: [0.5, 0] },
      target: { id: targetId, anchor: [0.5, 0] },
      color,
      sag: 0.12,
      width: relation.strength >= 70 ? 3.2 : relation.strength <= 40 ? 1.8 : 2.5,
      label: relation.label ?? relation.type,
    };
    elements.push({
      id: `rp_${relation.id}`,
      type: 'rope',
      x: 0,
      y: 0,
      w: 0,
      h: 0,
      rotate: 0,
      z: nextZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: props as unknown as Record<string, unknown>,
    });
  }
}

function placeHypotheses(
  hypotheses: Hypothesis[],
  nodeIdByRef: Map2,
  elements: GfxElement[],
  nextZ: () => string
): void {
  const size = DEFAULT_SIZE.note;
  hypotheses.slice(0, 12).forEach((hypothesis, index) => {
    const id = `hy_${hypothesis.id}`;
    const x = -940 + (index % 4) * 300;
    const y = -830 + Math.floor(index / 4) * 180;
    nodeIdByRef.set(hypothesis.id, id);
    pushNote(
      elements,
      id,
      { x, y, w: size.w, h: size.h },
      nextZ,
      {
        title: `研判结论｜${hypothesis.createdBy === 'ai' ? '系统' : '人工'}`,
        text: `${hypothesis.statement}\n置信度 ${(hypothesis.confidence * 100).toFixed(0)}% · ${
          hypothesis.status === 'supported' ? '已支持' : hypothesis.status === 'refuted' ? '已排除' : '待验证'
        }`,
        color: hypothesis.createdBy === 'ai' ? 'purple' : 'default',
      },
      { kind: 'hypothesis', id: hypothesis.id }
    );
  });
}

export { uid as intelNodeId };
export type { ConnectorProps };